import React, { useState } from 'react';
import { Box, Typography, Container, Alert, MenuItem } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import WbTextField from '../../../components/common/WbTextField';
import WbButton from '../../../components/common/WbButton';
import WbLoader from '../../../components/common/WbLoader';
import KioskLayout from '../../../components/kiosk/KioskLayout';
import { useKiosk } from '../../../contexts/KioskContext';
import kioskApi from '../../../services/api/kiosk/kioskApi';
import paths from '../../../routes/paths';
import '../KioskStyles.scss';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const CURRENT_YEAR = new Date().getFullYear();
const MIN_BIRTH_YEAR = CURRENT_YEAR - 25; // e.g. ~25 years ago for school-age range
const MAX_BIRTH_YEAR = CURRENT_YEAR - 4;  // e.g. at least 4 years old

export default function ParentVerify() {
  const { schoolSlug } = useParams();
  const location = useLocation();
  const { school, loading: schoolLoading } = useKiosk();
  const navigate = useNavigate();
  const child = location.state?.child;

  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  React.useEffect(() => {
    if (!child) {
      navigate(paths.kioskParentSearch.replace(':schoolSlug', schoolSlug));
    }
  }, [child, navigate, schoolSlug]);

  const handleVerify = async () => {
    if (!day || !month || !year) {
      setError('Please enter full date of birth');
      return;
    }

    const dayNum = parseInt(day, 10);
    const yearNum = parseInt(year, 10);
    const monthIndex = MONTHS.indexOf(month);

    if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
      setError('Please enter a valid day (1-31)');
      return;
    }
    if (monthIndex === -1) {
      setError('Please select a valid month');
      return;
    }
    if (isNaN(yearNum) || yearNum < MIN_BIRTH_YEAR || yearNum > MAX_BIRTH_YEAR) {
      setError(`Please enter a valid year (${MIN_BIRTH_YEAR}–${MAX_BIRTH_YEAR})`);
      return;
    }

    const monthNum = (monthIndex + 1).toString().padStart(2, '0');
    const dayStr = dayNum.toString().padStart(2, '0');
    const dateOfBirth = `${yearNum}-${monthNum}-${dayStr}`;

    setVerifying(true);
    setError(null);

    try {
      const result = await kioskApi.verifyParent(child.id, dateOfBirth);
      if (result.isValid) {
        // Store verified parent info
        localStorage.setItem('kiosk-verified-parent', JSON.stringify({
          parentId: result.parent.id,
          parentName: result.parent.name,
          childId: child.id,
          childName: child.childName,
        }));
        navigate(paths.kioskParentSurveys.replace(':schoolSlug', schoolSlug));
      } else {
        setError('Date of birth does not match. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (schoolLoading || !child) {
    return (
      <KioskLayout>
        <WbLoader />
      </KioskLayout>
    );
  }

  return (
    <KioskLayout>
      <Container maxWidth="md">
        <div className="kiosk-title">
          Verify Date of Birth
        </div>
        <div className="kiosk-subtitle">
          Please enter the date of birth for {child.childName}
        </div>

        <Box className="kiosk-date-inputs">
          <WbTextField
            label="Day"
            value={day}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 2);
              setDay(value);
              setError(null);
            }}
            className="kiosk-date-field"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2 }}
          />
          <WbTextField
            select
            label="Month"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              setError(null);
            }}
            className="kiosk-date-field "            
          >
            {MONTHS.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </WbTextField>
          <WbTextField
            label="Year"
            value={year}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setYear(value);
              setError(null);
            }}
            placeholder={`e.g. ${MAX_BIRTH_YEAR}`}
            className="kiosk-date-field kiosk-text-field-large"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4, min: MIN_BIRTH_YEAR, max: MAX_BIRTH_YEAR }}
          />
        </Box>

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <WbButton
            CustomButtonText="Back"
            onClick={() => navigate(paths.kioskParentSearch.replace(':schoolSlug', schoolSlug))}
            className="kiosk-button-large"            
          />
          <WbButton
            CustomButtonText={verifying ? 'Verifying...' : 'Continue'}
            onClick={handleVerify}
            disabled={verifying || !(day && month && year)}
            className="kiosk-button-large"
          />
        </Box>
      </Container>
    </KioskLayout>
  );
}
